// Engineer status — who counts as a repair engineer, and whose bookings count
// as repair output.
//
//   active  default; stores no row
//   left    a real engineer who has gone; history is kept, diagnostics are not
//   admin   not a repair engineer; bookings are admin work and never count
//
// Status is stored against the rota name. History rows arrive under whatever
// spelling the KPI export used, so every lookup resolves through the alias
// index first.

export const ENGINEER_STATUSES = ["active", "left", "admin"];

// Matches historyConsistency.js's normalizeName (digits kept), not App.jsx's
// normalizeEngineerName (letters only). The consistency report is this module's
// heaviest consumer.
function normalizeStatusName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function cleanStatus(value) {
  const status = String(value || "")
    .trim()
    .toLowerCase();
  return ENGINEER_STATUSES.includes(status) ? status : "active";
}

function aliasKeys(row) {
  return (row?.aliases || [])
    .map((entry) => (typeof entry === "string" ? entry : entry?.alias))
    .map(normalizeStatusName)
    .filter(Boolean);
}

// Groups every spelling of one person — the rota name plus each alias — so a
// status set on any one of them answers for all of them.
function buildIdentityGroups(aliasRows) {
  const groups = [];
  (aliasRows || []).forEach((row) => {
    const canonical = normalizeStatusName(row?.engineer);
    if (!canonical) return;
    groups.push(new Set([canonical, ...aliasKeys(row)]));
  });
  return groups;
}

export function buildEngineerStatusIndex(statusRows, aliasRows) {
  const groups = buildIdentityGroups(aliasRows);

  // Track which groups each normalized key belongs to. A key is ambiguous
  // if it appears in more than one group — this guards against the data-
  // integrity fault of an alias shared across different canonical engineers.
  const keyToGroups = new Map();
  groups.forEach((group) => {
    group.forEach((key) => {
      if (!keyToGroups.has(key)) {
        keyToGroups.set(key, []);
      }
      keyToGroups.get(key).push(group);
    });
  });

  const byKey = {};

  (statusRows || []).forEach((row) => {
    const key = normalizeStatusName(row?.engineer);
    if (!key) return;
    const status = cleanStatus(row?.status);
    if (status === "active") return; // active stores nothing

    // The status row explicitly named this key, so it always gets the status.
    byKey[key] = status;

    // Propagate to other spellings only if this key is unambiguous
    // (appears in exactly one identity group). An ambiguous key means
    // the same alias is used for two different canonical engineers —
    // a data-integrity fault — so we don't propagate through it.
    const containingGroups = keyToGroups.get(key) || [];
    if (containingGroups.length === 1) {
      const group = containingGroups[0];
      group.forEach((member) => {
        byKey[member] = status;
      });
    }
  });

  return { byKey };
}

export function engineerStatusFor(name, index) {
  const key = normalizeStatusName(name);
  if (!key) return "active";
  return cleanStatus(index?.byKey?.[key]);
}

/** True when this person's parts must not count at all — admin only. */
export function isExcludedFromHistory(name, index) {
  return engineerStatusFor(name, index) === "admin";
}

/** True when this person is neither offered nor diagnosed — left and admin. */
export function isExcludedFromActive(name, index) {
  const status = engineerStatusFor(name, index);
  return status === "left" || status === "admin";
}
